type Fn = (...args: any[]) => any

interface Shimmer {
  wrap<T extends Fn>(
    module: Record<string, any>,
    name: string,
    wrapper: (original: T) => T
  ): T | undefined
  massWrap<T extends Fn>(
    modules: Record<string, any>[],
    names: string[],
    wrapper: (original: T) => T
  ): void
  unwrap<T extends Fn>(module: Record<string, any>, name: string): T | undefined
  massUnwrap(modules: Record<string, any>[], names: string[]): void
}

export const shimmer: Shimmer = {
  wrap<T extends Fn>(
    module: Record<string, any>,
    name: string,
    wrapper: (original: T) => T
  ): T | undefined {
    if (!module || !module[name]) {
      console.error(`No original function ${name} to wrap`)
      return
    }

    if (!wrapper) {
      console.error('No wrapper function')
      console.trace()
      return
    }

    if (module[name].__wrapped) {
      console.error(`Function ${name} already wrapped`)
      return
    }

    const original = module[name] as T
    const wrapped = wrapper(original) as any

    wrapped.__wrapped = true
    wrapped.__unwrap = () => {
      if (module[name] === wrapped) {
        module[name] = original
        delete wrapped.__unwrap
      }
    }

    module[name] = wrapped

    return wrapped
  },

  massWrap<T extends Fn>(
    modules: Record<string, any>[],
    names: string[],
    wrapper: (original: T) => T
  ): void {
    if (!modules) {
      console.error('Must provide one or more modules to patch')
      console.trace()
      return
    }

    if (!Array.isArray(modules)) {
      modules = [modules]
    }

    if (!(names && Array.isArray(names))) {
      console.error('Must provide one or more functions to wrap on modules')
      return
    }

    modules.forEach((module) => {
      names.forEach((name) => {
        shimmer.wrap<T>(module, name, wrapper)
      })
    })
  },

  unwrap<T extends Fn>(
    module: Record<string, any>,
    name: string
  ): T | undefined {
    if (!module || !module[name]) {
      console.error('No function to unwrap.')
      return
    }

    if (!module[name].__unwrap) {
      console.error(`Function ${name} has not been wrapped`)
      return
    }

    module[name].__unwrap()
    return module[name] as T
  },

  massUnwrap(modules: Record<string, any>[], names: string[]): void {
    if (!modules) {
      console.error('Must provide one or more modules to patch')
      console.trace()
      return
    }

    if (!Array.isArray(modules)) {
      modules = [modules]
    }

    if (!(names && Array.isArray(names))) {
      console.error('Must provide one or more functions to unwrap on modules')
      return
    }

    modules.forEach((module) => {
      names.forEach((name) => {
        shimmer.unwrap(module, name)
      })
    })
  },
}
